import React, { useState } from "react";
import { SectionEntity } from "@/database";
import { fetchHostSubSectionAPI } from "@/database/media/api";

interface AddedInSectionRowProps {
  sectionMsId: number;
  title: string;
  fullPath: string;
  hostId: number;
  showPriorityPanel?: boolean;
  onPrioritySectionSelect: (section: SectionEntity) => void;
  selectedHost: string;
  onSectionClick: (section: SectionEntity) => void;
  disablePlayButton?: boolean;
}

const AddedInSectionRow: React.FC<AddedInSectionRowProps> = ({
  sectionMsId,
  title,
  fullPath,
  hostId,
  showPriorityPanel,
  onPrioritySectionSelect,
  selectedHost,
  onSectionClick,
  disablePlayButton = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (showPriorityPanel) {
      // First, call denmark.search to find the parentid for this section
      setIsLoading(true);
      try {
        const searchResponse = await fetchHostSubSectionAPI({
          keyword: String(sectionMsId),
          hostid: String(selectedHost),
        });
        let parentId = 0;
        if (searchResponse.status === 200 && searchResponse.data?.length > 0) {
          // Find the matching section by id from the search results
          const matchedSection = searchResponse.data.find(
            (item: any) => item.id === sectionMsId
          );
          if (matchedSection) {
            parentId = matchedSection.parentid ?? 0;
          }
        }

        const pseudoSection: SectionEntity = {
          msId: sectionMsId,
          name: title,
          path: fullPath,
          childSections: [],
          parents: [{ parentId, hostId, activationStatus: "" }],
        } as SectionEntity;

        onPrioritySectionSelect(pseudoSection);
      } finally {
        setIsLoading(false);
      }
    } else {
      const pseudoSection: SectionEntity = {
        msId: sectionMsId,
        name: title,
        path: fullPath,
        childSections: [],
        parents: [{ parentId: 0, hostId, activationStatus: "" }],
      } as SectionEntity;
      onSectionClick(pseudoSection);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="btn w-100 d-flex align-items-center text-start "
      style={{
        minHeight: "30px",
        padding: "0px 0px",
        marginBottom: "1px",
        // paddingLeft: "10",
        backgroundColor: isHovered ? "#f3f6f8" : "transparent",
        color: "#565b60",
        fontSize: "18px",
        fontWeight: 500,
        transition:
          "background-color 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease",
        transform: isHovered ? "translateY(-2px)" : "translateY(0)",
        boxShadow: isHovered ? "0 4px 10px rgba(0,0,0,0.1)" : "none",
        borderRadius: isHovered ? "6px" : "0",
        zIndex: isHovered ? 1 : "auto",
        textTransform: "none",
        position: "relative",
      }}
      onMouseEnter={() => !disablePlayButton && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (!disablePlayButton) handleClick();
      }}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disablePlayButton) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span
        className=" d-flex align-items-center justify-content-center"
        style={{ width: "28px", minWidth: "28px" }}
      >
        <i
          className="bi bi-file-earmark"
          style={{ fontSize: "22px", color: "#555f6b" }}
        />
      </span>
      <span className="text-truncate">{title}</span>
      <span className="ms-auto d-flex align-items-center">
        <button
          type="button"
          disabled={disablePlayButton}
          className="d-flex align-items-center justify-content-center border p-0 bg-white rounded-1"
          style={{
            width: "28px",
            height: "28px",
            opacity: isHovered ? 1 : 0,
            visibility: isHovered ? "visible" : "hidden",
            pointerEvents: isHovered && !disablePlayButton ? "auto" : "none",
            transition: "none",
            borderColor: disablePlayButton ? "#adb5bd" : "#1a87d4",
            color: disablePlayButton ? "#adb5bd" : "#1a87d4",
            cursor: disablePlayButton ? "not-allowed" : "pointer",
          }}
          aria-label={`Select ${title}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!disablePlayButton) handleClick();
          }}
        >
          {isLoading ? (
            <span
              className="spinner-border spinner-border-sm text-primary"
              style={{ width: "12px", height: "12px", borderWidth: "2px" }}
            />
          ) : (
            <i
              className={
                disablePlayButton
                  ? "bi bi-play-fill text-secondary"
                  : "bi bi-play-fill text-primary"
              }
              style={{ fontSize: "14px" }}
            />
          )}
        </button>
      </span>
    </div>
  );
};

export default AddedInSectionRow;
